import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from '../hooks/useAuth';

interface StaffSocketContextType {
  socket: Socket | null;
  isConnected: boolean;
}

const StaffSocketContext = createContext<StaffSocketContextType>({
  socket: null,
  isConnected: false,
});

export const StaffSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token, user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    console.log('StaffSocketProvider: rendered', { hasToken: !!token, user: user?.prenom, role: user?.role });

    if (!token || !user) {
      console.log('StaffSocketProvider: Missing token or user, waiting...');
      return;
    }

    const apiUrl = (import.meta.env.VITE_API_URL || 'http://localhost:2000/api/v1').replace(/\/$/, '');
    const baseUrl = apiUrl.split('/api/v1')[0];

    console.log(`StaffSocketProvider: Connection attempt to ${baseUrl}/staff-chat`);

    const socket = io(`${baseUrl}/staff-chat`, {
      auth: { token },
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 10,
      timeout: 10000
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('StaffSocketProvider: ✅ Connected to staff-chat namespace');
      setIsConnected(true);
    });

    socket.on('disconnect', (reason) => {
      console.log('StaffSocketProvider: ❌ Disconnected:', reason);
      setIsConnected(false);
    });

    socket.on('connect_error', (err) => {
      console.error('StaffSocketProvider: ⚠️ Connection error:', err.message);
    });

    return () => {
      console.log('StaffSocketProvider: Cleaning up...');
      if (socket) {
        socket.disconnect();
        socketRef.current = null;
      }
    };
  }, [token, user]);

  const value = React.useMemo(() => ({ 
    socket: socketRef.current, 
    isConnected 
  }), [isConnected]);

  return (
    <StaffSocketContext.Provider value={value}>
      {children}
    </StaffSocketContext.Provider>
  );
};

export const useStaffSocket = () => useContext(StaffSocketContext);
